import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Collapse from '@mui/material/Collapse';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

import { minToHm } from '../utils/time';
import { TrainerSection } from '../sections/TrainerSection';
import { CustomerSection } from '../sections/CustomerSection';
import { ActivityOnlySection } from '../sections/ActivityOnlySection';
import { sessionsApi } from 'sectors/pilates/sessions/api/sessionsApi';

// ✅ paid api (senin verdiğin)

function numOrNull(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function clean(x) {
  const s = (x ?? '').toString().trim().replace(/\s+/g, ' ');
  return s.length ? s : '';
}
function digitsOnly(x) {
  return (x ?? '').toString().replace(/[^\d+]/g, '');
}

function pickMsg(err) {
  return err?.data?.message || err?.response?.data?.message || err?.message || 'İşlem başarısız.';
}

// decision helpers (backend shape: {allowed, blocks[], warnings[]})
const normList = (arr) =>
  (Array.isArray(arr) ? arr : []).map((x) => ({
    code: x?.code || null,
    title: x?.title || null,
    message: x?.message || (typeof x === 'string' ? x : JSON.stringify(x)),
  }));

function DecisionDialog({ open, onClose, onProceed, decision, busy = false, tokens }) {
  const theme = useMuiTheme();
  const blocks = normList(decision?.blocks);
  const warnings = normList(decision?.warnings);
  const isBlock = blocks.length > 0;

  const items = isBlock ? blocks : warnings;

  const tone = isBlock ? theme.palette.error.main : theme.palette.warning.main;
  const icon = isBlock ? 'block' : 'warning';

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1.2 }}>
        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MDBox
            sx={{
              width: 40,
              height: 40,
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              background: alpha(tone, 0.12),
              border: `1px solid ${alpha(tone, 0.22)}`,
              flex: '0 0 auto',
            }}
          >
            <Icon sx={{ color: tone, fontSize: 20 }}>{icon}</Icon>
          </MDBox>

          <MDBox sx={{ minWidth: 0 }}>
            <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
              {isBlock ? 'İşlem Engellendi' : 'Uyarılar'}
            </MDTypography>
            <MDTypography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.35 }}>
              {isBlock
                ? 'Bu seans kaydedilemez. Aşağıdaki engelleri düzelt.'
                : 'Devam edersen seans kaydedilecek. İstersen yine de kaydedebilirsin.'}
            </MDTypography>
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <MDBox sx={{ display: 'grid', gap: 1 }}>
          {items.length ? (
            items.map((x, i) => (
              <MDBox
                key={i}
                sx={{
                  p: 1.2,
                  borderRadius: '16px',
                  border: `1px solid ${alpha(tone, 0.25)}`,
                  background: alpha(tone, tokens?.isDark ? 0.08 : 0.06),
                }}
              >
                <MDTypography variant="button" fontWeight="bold" sx={{ display: 'block' }}>
                  {x.title || (isBlock ? 'Engel' : 'Uyarı')}
                </MDTypography>
                <MDTypography variant="caption" sx={{ opacity: 0.82, display: 'block', mt: 0.4 }}>
                  {x.message}
                </MDTypography>
              </MDBox>
            ))
          ) : (
            <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
              Bir mesaj bulunamadı.
            </MDTypography>
          )}
        </MDBox>
      </DialogContent>

      <DialogActions sx={{ px: 2.2, py: 1.6, gap: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <MDButton
          variant="outlined"
          color="dark"
          onClick={onClose}
          disabled={busy}
          sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 800 }}
        >
          Kapat
        </MDButton>

        {!isBlock ? (
          <MDButton
            variant="contained"
            color="warning"
            onClick={onProceed}
            disabled={busy}
            sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900 }}
          >
            {busy ? 'Kaydediliyor...' : 'Yine de Kaydet'}
          </MDButton>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export function SessionCreatePaidModal({
  open,
  onClose,
  onBack,
  // ✅ artık modal kendi paid endpoint’lerine atıyor.
  // onCreate / onCreated yine destekli ama opsiyon
  onCreated,

  dayISO,
  startMin,
  defaultTrainerId = 'ALL',
  tokens,
  trainers = [],
  staffLoading = false,
  staffError = false,
}) {
  const theme = useMuiTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // 0 = Üye, 1 = Misafir
  const [mode, setMode] = useState(0);

  // trainer
  const [trainerId, setTrainerId] = useState('');

  // activity
  const [activityId, setActivityId] = useState('');
  const [activities, setActivities] = useState([]); // ActivityOnlySection'dan gelir

  // member selection
  const [memberIds, setMemberIds] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);

  // ✅ üye seçilmeden kaydetmek için: artık toggle ile açılır
  const [saveAsAnon, setSaveAsAnon] = useState(false);
  const [anonName, setAnonName] = useState('');
  const [anonPhone, setAnonPhone] = useState('');

  // guest (Misafir tab'ında)
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');

  // paid
  // ✅ ödeme alındı mı + alınan tutar
  const [priceTry, setPriceTry] = useState('');
  const [durationMin, setDurationMin] = useState(60);

  // ✅ ödeme alındı mı + alınan tutar
  const [paidNow, setPaidNow] = useState(false);
  const [paidTry, setPaidTry] = useState('');

  // note
  const [note, setNote] = useState('');

  // ✅ validate decision dialog
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decision, setDecision] = useState(null);

  // ✅ busy
  const [saving, setSaving] = useState(false);

  // payload ref (warning dialog “yine de kaydet” için)
  const lastPayloadRef = useRef(null);

  // reset on open
  useEffect(() => {
    if (!open) return;

    setMode(0);
    setTrainerId('');

    setActivityId('');
    setActivities([]);

    setMemberIds([]);
    setSelectedMembers([]);

    setSaveAsAnon(false);
    setAnonName('');
    setAnonPhone('');

    setGuestName('');
    setGuestPhone('');

    setPriceTry('');
    setDurationMin(60);
    setPriceTry('');
    setDurationMin(60);
    setNote('');

    setDecision(null);
    setDecisionOpen(false);
    setSaving(false);
    lastPayloadRef.current = null;
  }, [open]);

  // default trainer
  useEffect(() => {
    if (!open) return;
    const list = Array.isArray(trainers) ? trainers : [];
    const tId =
      defaultTrainerId !== 'ALL'
        ? String(defaultTrainerId)
        : list[0]?.id != null
        ? String(list[0].id)
        : '';
    setTrainerId((prev) => prev || tId);
  }, [open, defaultTrainerId, trainers]);

  const title = dayISO ? `${dayISO} • ${minToHm(startMin)}` : 'Ücretli Seans';

  const selectedTrainerName = useMemo(() => {
    const t = (trainers || []).find((x) => String(x.id) === String(trainerId));
    return clean(t?.name) || '';
  }, [trainerId, trainers]);

  const selectedActivityName = useMemo(() => {
    const a = (activities || []).find((x) => String(x?.id) === String(activityId));
    return clean(a?.name) || '';
  }, [activities, activityId]);

  const hasMembers = (memberIds?.length || 0) > 0;

  // ✅ üye seçilince “isimle kaydet” otomatik kapanır
  useEffect(() => {
    if (!open) return;
    if (hasMembers) {
      setSaveAsAnon(false);
      setAnonName('');
      setAnonPhone('');
    }
  }, [open, hasMembers]);

  // capacity (backend: capacity >= participants.size)
  const capacity = useMemo(() => {
    if (mode === 0) return Math.max(1, hasMembers ? memberIds.length : 1);
    return 1;
  }, [mode, hasMembers, memberIds.length]);

  // ✅ backend DTO uyumu: priceTry root’ta, participant içinde yok
  const buildParticipants = () => {
    const paid = paidNow ? numOrNull(paidTry) ?? 0 : 0;

    if (mode === 0) {
      if (hasMembers) {
        return memberIds.map((mid) => ({
          memberId: Number(mid),
          fullName: null,
          phone: null,
          paidTry: paid, // ✅
        }));
      }
      return [
        {
          memberId: null,
          fullName: clean(anonName),
          phone: digitsOnly(anonPhone) || null,
          paidTry: paid, // ✅
        },
      ];
    }

    return [
      {
        memberId: null,
        fullName: clean(guestName),
        phone: digitsOnly(guestPhone) || null,
        paidTry: paid, // ✅
      },
    ];
  };

  const canSave = useMemo(() => {
    if (!open) return false;
    if (!dayISO || typeof startMin !== 'number') return false;
    if (!trainerId) return false;
    if (!activityId) return false;

    const p = numOrNull(priceTry);
    if (p == null || p <= 0) return false;
    const pay = paidNow ? numOrNull(paidTry) ?? 0 : 0;
    if (paidNow) {
      if (pay <= 0) return false;
      if (p != null && pay > p) return false;
    }
    if (mode === 0) {
      // Üye tabı: üye seçiliyse OK, değilse “isimle kaydet” açılmalı + ad zorunlu
      if (hasMembers) return true;
      return saveAsAnon && !!clean(anonName);
    }

    // Misafir tabı: isim zorunlu
    return !!clean(guestName);
  }, [
    open,
    dayISO,
    startMin,
    trainerId,
    activityId,
    priceTry,
    paidNow,
    paidTry,
    mode,
    hasMembers,
    saveAsAnon,
    anonName,
    guestName,
  ]);

  const buildPayload = () => {
    const p = numOrNull(priceTry);

    return {
      dayISO,
      startMin: Number(startMin),
      durationMin: Number(durationMin) || 60,

      trainerId: Number(trainerId),
      trainerName: clean(selectedTrainerName) || null,

      activityId: Number(activityId),
      activityName: clean(selectedActivityName) || null,

      capacity,

      priceTry: p, // ✅ root
      note: clean(note) || null,

      participants: buildParticipants(),
    };
  };

  const doCreate = async (payload) => {
    // ✅ iki opsiyon: dışarıdan onCreate verilmişse onu da çağır (sync/async)
    // ama asıl doğru endpoint: sessionsApi.createPaidSession

    await sessionsApi.createPaidSession(payload);
  };

  const doValidate = async (payload) => {
    return await sessionsApi.validatePaidSession(payload);
  };

  const handleSave = async () => {
    if (!canSave || saving) return;

    const payload = buildPayload();
    lastPayloadRef.current = payload;

    setSaving(true);
    try {
      // ✅ önce validate
      const dec = await doValidate(payload);
      setDecision(dec);

      const blocks = Array.isArray(dec?.blocks) ? dec.blocks : [];
      const warnings = Array.isArray(dec?.warnings) ? dec.warnings : [];

      if (blocks.length || warnings.length) {
        setDecisionOpen(true);
        return;
      }

      // ✅ uyarı yok -> direkt create
      await doCreate(payload);

      onClose?.();
      await onCreated?.();
    } catch (err) {
      console.error('[SessionCreatePaidModal] save FAILED', err);
      alert(pickMsg(err) || 'Ücretli seans kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleProceedDespiteWarnings = async () => {
    if (saving) return;
    const payload = lastPayloadRef.current || buildPayload();

    setSaving(true);
    try {
      await doCreate(payload);
      setDecisionOpen(false);
      onClose?.();
      await onCreated?.();
    } catch (err) {
      console.error('[SessionCreatePaidModal] create despite warnings FAILED', err);
      alert(pickMsg(err) || 'Ücretli seans kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    // tiki açınca otomatik seans ücretini doldur
    if (paidNow) {
      const p = numOrNull(priceTry);
      setPaidTry(String(p != null && p > 0 ? p : ''));
    } else {
      setPaidTry('');
    }
  }, [open, paidNow, priceTry]);

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : '22px',
            overflow: 'hidden',
            background: tokens?.isDark ? alpha('#0b1220', 0.92) : alpha('#ffffff', 0.98),
            border: `1px solid ${tokens?.border || theme.palette.divider}`,
            boxShadow: fullScreen
              ? 'none'
              : '0 22px 60px rgba(0,0,0,.16), 0 10px 22px rgba(0,0,0,.12)',
            backgroundImage: 'none',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: fullScreen ? 'rgba(0,0,0,.55)' : 'rgba(0,0,0,.45)',
            backdropFilter: 'blur(6px)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1.2} sx={{ minWidth: 0 }}>
              <MDBox
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '16px',
                  display: 'grid',
                  placeItems: 'center',
                  background: alpha(theme.palette.warning.main, 0.12),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.22)}`,
                  flex: '0 0 auto',
                }}
              >
                <Icon sx={{ fontSize: 20, color: theme.palette.warning.main }}>payments</Icon>
              </MDBox>

              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                  Ücretli Seans
                </MDTypography>
                <MDTypography variant="caption" sx={{ opacity: 0.78, display: 'block', mt: 0.35 }}>
                  {title}
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox display="flex" gap={0.6}>
              <MDButton
                variant="text"
                color="secondary"
                onClick={saving ? undefined : onBack}
                disabled={saving}
                sx={{ minWidth: 44, width: 44, height: 44, borderRadius: '14px' }}
              >
                <Icon>arrow_back</Icon>
              </MDButton>
              <MDButton
                variant="text"
                color="secondary"
                onClick={saving ? undefined : onClose}
                disabled={saving}
                sx={{ minWidth: 44, width: 44, height: 44, borderRadius: '14px' }}
              >
                <Icon>close</Icon>
              </MDButton>
            </MDBox>
          </MDBox>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 1.6, pb: 1.3 }}>
          <MDBox sx={{ display: 'grid', gap: 1.1 }}>
            <TrainerSection
              sx={tokens?.card}
              tokens={tokens}
              trainerId={trainerId}
              setTrainerId={setTrainerId}
              trainers={trainers}
              loading={staffLoading}
              error={staffError}
            />

            <ActivityOnlySection
              sx={tokens?.card}
              tokens={tokens}
              activityId={activityId}
              setActivityId={setActivityId}
              onItemsLoaded={setActivities}
            />

            {/* Üye / Misafir */}
            <MDBox sx={{ ...tokens?.card, p: 1.2, borderRadius: '18px' }}>
              <Tabs
                value={mode}
                onChange={(_, v) => setMode(v)}
                variant="fullWidth"
                sx={{
                  mb: 1,
                  minHeight: 38,
                  '& .MuiTab-root': { minHeight: 38, fontWeight: 900, textTransform: 'none' },
                }}
              >
                <Tab label="Üye" />
                <Tab label="Misafir" />
              </Tabs>

              {mode === 0 ? (
                <>
                  <CustomerSection
                    tokens={tokens}
                    trainerId={trainerId}
                    selectedIds={memberIds}
                    setSelectedIds={setMemberIds}
                    onSelectedMembersChange={setSelectedMembers}
                  />

                  {/* ✅ Üye seçiliyken anon alanları asla açık değil */}
                  {!hasMembers ? (
                    <MDBox sx={{ mt: 1 }}>
                      <Collapse in={saveAsAnon} timeout={180}>
                        <MDBox sx={{ mt: 1, display: 'grid', gap: 1 }}>
                          <MDInput
                            label="Ad Soyad"
                            value={anonName}
                            onChange={(e) => setAnonName(e.target.value)}
                            fullWidth
                          />
                          <MDInput
                            label="Telefon (opsiyonel)"
                            value={anonPhone}
                            onChange={(e) => setAnonPhone(e.target.value)}
                            fullWidth
                          />
                          <MDTypography
                            variant="caption"
                            sx={{ opacity: 0.7, fontWeight: 700, mt: 0.25 }}
                          >
                            Üye seçmezsen bu seans “isimle” kaydedilir (telefon zorunlu değil).
                          </MDTypography>
                        </MDBox>
                      </Collapse>
                    </MDBox>
                  ) : null}
                </>
              ) : (
                <MDBox sx={{ display: 'grid', gap: 1 }}>
                  <MDInput
                    label="Ad Soyad"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    fullWidth
                  />
                  <MDInput
                    label="Telefon (opsiyonel)"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    fullWidth
                  />
                </MDBox>
              )}
            </MDBox>

            {/* Ücret / Süre / Not */}
            <MDBox sx={{ ...tokens?.card, p: 1.2, borderRadius: '18px' }}>
              <MDBox sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <MDInput
                  type="number"
                  label="Ücret (₺) (kişi başı)"
                  value={priceTry}
                  onChange={(e) => setPriceTry(e.target.value)}
                  fullWidth
                />
                <MDInput
                  type="number"
                  label="Süre (dk)"
                  value={durationMin}
                  onChange={(e) => setDurationMin(Number(e.target.value || 60))}
                  fullWidth
                />
              </MDBox>
              <MDBox
                sx={{
                  mt: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={paidNow}
                      onChange={(e) => setPaidNow(e.target.checked)}
                      disabled={saving}
                    />
                  }
                  label="Ödeme alındı"
                />

                <MDBox
                  sx={{
                    width: 170,
                    opacity: paidNow ? 1 : 0.55,
                    pointerEvents: paidNow ? 'auto' : 'none',
                  }}
                >
                  <MDInput
                    type="number"
                    label="Ödenen (₺)"
                    value={paidTry}
                    onChange={(e) => setPaidTry(e.target.value)}
                    fullWidth
                  />
                </MDBox>
              </MDBox>

              <MDBox sx={{ mt: 1 }}>
                <MDInput
                  label="Not (opsiyonel)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                />
              </MDBox>

              {/* küçük özet */}
              <MDBox sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', opacity: 0.8 }}>
                <MDTypography variant="caption" fontWeight="bold">
                  Kapasite: {capacity}
                </MDTypography>
                <MDTypography variant="caption" fontWeight="bold">
                  Katılımcı:{' '}
                  {mode === 0
                    ? hasMembers
                      ? memberIds.length
                      : saveAsAnon
                      ? 1
                      : 0
                    : clean(guestName)
                    ? 1
                    : 0}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            p: 2,
            pt: 1.4,
            gap: 1,
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: fullScreen ? 'column' : 'row',
          }}
        >
          <MDButton
            variant="outlined"
            color="dark"
            onClick={onClose}
            disabled={saving}
            fullWidth={fullScreen}
            sx={{ borderRadius: '16px', textTransform: 'none', fontWeight: 900, height: 44 }}
          >
            Vazgeç
          </MDButton>

          <MDButton
            variant="gradient"
            color="warning"
            onClick={handleSave}
            disabled={!canSave || saving}
            fullWidth={fullScreen}
            sx={{ borderRadius: '16px', textTransform: 'none', fontWeight: 900, height: 44 }}
          >
            {saving ? 'Kontrol ediliyor...' : 'Kaydet'}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ✅ validate warnings/blocks dialog */}
      <DecisionDialog
        open={decisionOpen}
        onClose={() => setDecisionOpen(false)}
        onProceed={handleProceedDespiteWarnings}
        decision={decision}
        busy={saving}
        tokens={tokens}
      />
    </>
  );
}
